import React, { useState, useEffect } from 'react';
import api from '../services/api';

const EVENEMENT_BADGE = {
  CONNEXION_OK:     { cls: 'badge-success', label: 'Connexion réussie' },
  CONNEXION_ECHEC:  { cls: 'badge-error',   label: 'Échec connexion' },
  MFA_ECHEC:        { cls: 'badge-warning',  label: 'Échec MFA' },
  DECONNEXION:      { cls: 'badge-grey',     label: 'Déconnexion' },
};

export default function Journal() {
  const [entries, setEntries]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filtre, setFiltre]         = useState('');
  const [filtreUser, setFiltreUser] = useState('');
  const [stats, setStats]           = useState(null);

  useEffect(() => { loadJournal(); }, []);

  const loadJournal = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 200 });
      if (filtre)     params.append('evenement', filtre);
      if (filtreUser) params.append('user_id', filtreUser);
      const [j, s] = await Promise.all([
        api.get(`/admin/journal-connexions?${params}`),
        api.get('/admin/stats'),
      ]);
      setEntries(j.data);
      setStats(s.data);
    } catch (e) {} finally { setLoading(false); }
  };

  useEffect(() => { loadJournal(); }, [filtre]);

  // Calcul statistiques rapides
  const nbOk     = entries.filter(e => e.evenement === 'CONNEXION_OK').length;
  const nbEchec  = entries.filter(e => e.evenement === 'CONNEXION_ECHEC').length;
  const nbMfa    = entries.filter(e => e.evenement === 'MFA_ECHEC').length;

  const formatDate = (dt) => {
    if (!dt) return '—';
    const d = new Date(dt);
    return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const parseUA = (ua) => {
    if (!ua) return '—';
    if (ua.includes('Chrome'))  return '🌐 Chrome';
    if (ua.includes('Firefox')) return '🦊 Firefox';
    if (ua.includes('Safari'))  return '🧭 Safari';
    if (ua.includes('Edge'))    return '🔷 Edge';
    return ua.slice(0, 30) + '…';
  };

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Journal des connexions</div>
        <button className="btn btn-outline" style={{ fontSize: '0.72rem' }} onClick={loadJournal}>
          🔄 Actualiser
        </button>
      </div>
      <div className="page-content">

        {/* KPI */}
        <div className="kpi-grid" style={{ marginBottom: 20 }}>
          <div className="kpi-card" style={{ borderTopColor: 'var(--ecf-success)' }}>
            <div className="kpi-label">Connexions réussies</div>
            <div className="kpi-value" style={{ color: 'var(--ecf-success)' }}>{nbOk}</div>
            <div style={{ fontSize: '0.72rem', color: '#999', marginTop: 4 }}>sur les 200 dernières</div>
          </div>
          <div className="kpi-card" style={{ borderTopColor: 'var(--ecf-error)' }}>
            <div className="kpi-label">Échecs mot de passe</div>
            <div className="kpi-value" style={{ color: nbEchec > 5 ? 'var(--ecf-error)' : 'var(--ecf-black)' }}>
              {nbEchec}
            </div>
            {nbEchec > 5 && <div style={{ fontSize: '0.72rem', color: 'var(--ecf-error)', marginTop: 4, fontWeight: 700 }}>⚠️ Activité suspecte</div>}
          </div>
          <div className="kpi-card" style={{ borderTopColor: 'var(--ecf-warning)' }}>
            <div className="kpi-label">Échecs MFA</div>
            <div className="kpi-value" style={{ color: nbMfa > 3 ? 'var(--ecf-warning)' : 'var(--ecf-black)' }}>
              {nbMfa}
            </div>
          </div>
          {stats && (
            <div className="kpi-card">
              <div className="kpi-label">Utilisateurs actifs</div>
              <div className="kpi-value">{stats.nb_users}</div>
            </div>
          )}
        </div>

        {/* Filtres */}
        <div className="card" style={{ padding: '14px 20px', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <div className="form-label" style={{ marginBottom: 4 }}>Filtrer par événement</div>
              <select className="form-input" value={filtre}
                onChange={e => setFiltre(e.target.value)} style={{ margin: 0, width: 200 }}>
                <option value="">Tous les événements</option>
                <option value="CONNEXION_OK">Connexions réussies</option>
                <option value="CONNEXION_ECHEC">Échecs de connexion</option>
                <option value="MFA_ECHEC">Échecs MFA</option>
                <option value="DECONNEXION">Déconnexions</option>
              </select>
            </div>
            <button className="btn btn-primary" onClick={loadJournal} style={{ padding: '9px 16px' }}>
              Filtrer
            </button>
            {filtre && (
              <button className="btn btn-outline" onClick={() => setFiltre('')} style={{ padding: '9px 16px' }}>
                Effacer
              </button>
            )}
          </div>
        </div>

        {/* Tableau */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ecf-mid-grey)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="card-title" style={{ margin: 0 }}>
              Historique des connexions ({entries.length} entrées)
            </div>
            <span style={{ fontSize: '0.72rem', color: '#999' }}>200 dernières entrées</span>
          </div>

          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#999' }}>Chargement...</div>
          ) : entries.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#999' }}>Aucune entrée dans le journal</div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Date / Heure</th>
                    <th>Utilisateur</th>
                    <th>Rôle</th>
                    <th>Événement</th>
                    <th>Adresse IP</th>
                    <th>Navigateur</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(e => {
                    const badge = EVENEMENT_BADGE[e.evenement] || { cls: 'badge-grey', label: e.evenement };
                    return (
                      <tr key={e.id} style={{
                        background: e.evenement === 'CONNEXION_ECHEC' ? 'rgba(198,40,40,0.03)' :
                                    e.evenement === 'MFA_ECHEC' ? 'rgba(230,81,0,0.03)' : ''
                      }}>
                        <td style={{ fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          {formatDate(e.created_at)}
                        </td>
                        <td className="important">
                          {e.user_info
                            ? `${e.user_info.prenom} ${e.user_info.nom}`
                            : <span style={{ color: '#bbb', fontWeight: 400 }}>Inconnu</span>}
                        </td>
                        <td>
                          {e.user_info?.role
                            ? <span className="badge badge-info">{e.user_info.role}</span>
                            : '—'}
                        </td>
                        <td>
                          <span className={`badge ${badge.cls}`}>{badge.label}</span>
                        </td>
                        <td>
                          <code style={{ fontSize: '0.78rem', background: '#f4f4f4', padding: '2px 6px' }}>
                            {e.ip_address || '—'}
                          </code>
                        </td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--ecf-grey)' }}>
                          {parseUA(e.user_agent)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
